import React, { useState } from 'react';
import { ratingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const RateRide = ({ ride, onRatingSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await ratingAPI.rateRide(ride.id, rating, feedback || null);
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      if (onRatingSubmitted) {
        onRatingSubmitted();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  // Already rated
  if (ride.rating || submitted) {
    return (
      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <CardContent className="p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <span className="text-green-700 dark:text-green-400 font-medium">
              Ride rated
            </span>
            <div className="flex items-center gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${star <= (ride.rating || rating) ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <CardHeader>
        <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          Rate Your Ride
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          How was your experience with {ride.driver?.full_name || 'your driver'}?
        </p>

        {/* Star Rating */}
        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="transition-transform hover:scale-110"
              data-testid={`star-${star}`}
            >
              <Star
                className={`w-10 h-10 transition-colors ${
                  star <= (hoveredRating || rating)
                    ? 'text-amber-500 fill-amber-500'
                    : 'text-slate-300 dark:text-slate-600'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Rating Labels */}
        <div className="flex justify-between text-xs text-slate-400 mb-6 px-2">
          <span>Poor</span>
          <span>Excellent</span>
        </div>

        {/* Feedback */}
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Share your feedback (optional)"
          className="mb-4 resize-none"
          rows={3}
          data-testid="rating-feedback"
        />

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full"
          data-testid="submit-rating-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            'Submit Rating'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RateRide;
